import {
  Check as CheckIcon,
  Close as CloseIcon,
  DataObject as DataObjectIcon,
  Edit as EditIcon,
  Language as LanguageIcon,
  LocationOn as LocationIcon,
  Settings as SettingsIcon,
  Store as StoreIcon,
} from "@mui/icons-material";
import { Box, Button, Chip, Divider, Paper, Stack, Typography } from "@mui/material";
import { AdvancedMarker, APIProvider, Map, Marker } from "@vis.gl/react-google-maps";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { PageShell, ErrorState } from "@/components/common";
import { LoadingRing } from "@/components/common/LoadingRing";
import { LoadingState } from "@/components/common/LoadingState";
import { StoreLabelPreview } from "@/components/common/StoreLabelPreview";
import { paths } from "@/routes/paths";
import { Region, Store, StoreService } from "@/services/features/StoreService";
import { formatMetadataKey } from "@/utils/metadata";
import { formatStoreDate } from "@/utils/storeTime";

const sectionHeadingSx = {
  alignItems: "center",
  color: "primary.main",
  display: "flex",
  fontSize: { sm: "1.15rem", xs: "1.1rem" },
  gap: 1,
} as const;

const detailLabelSx = {
  color: "text.secondary",
  fontSize: "0.75rem",
  fontWeight: 700,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
} as const;

const twoColumnGridSx = {
  display: "grid",
  gap: 3,
  gridTemplateColumns: { md: "repeat(2, minmax(0, 1fr))", xs: "1fr" },
} as const;

const threeColumnGridSx = {
  display: "grid",
  gap: 3,
  gridTemplateColumns: { md: "repeat(3, minmax(0, 1fr))", xs: "1fr" },
} as const;

const GOOGLE_MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_KEY;
const GOOGLE_MAP_ID = import.meta.env.VITE_GOOGLE_MAP_ID;

const getTrimmedString = (value: unknown) => (typeof value === "string" ? value.trim() : "");

const getStoreAddress = (store: Store) =>
  getTrimmedString(store.address) ||
  [store.street, store.city, store.state, store.zip, store.country]
    .map(getTrimmedString)
    .filter(Boolean)
    .join(", ");

const getStorePosition = (store: Store) => {
  const geoPoint = store.geoPoint;
  if (!geoPoint) return null;

  const { lat, lon } = geoPoint;

  return Number.isFinite(lat) && Number.isFinite(lon) ? { lat, lng: lon } : null;
};

const hasDisplayValue = (value: unknown): value is string => Boolean(getTrimmedString(value));

interface DetailItemProps {
  helperText?: React.ReactNode;
  label: React.ReactNode;
  value: React.ReactNode;
}

const DetailItem = ({ helperText, label, value }: DetailItemProps) => (
  <Box>
    <Typography component="dt" sx={detailLabelSx}>
      {label}
    </Typography>
    <Typography component="dd" sx={{ m: 0, mt: 0.5, overflowWrap: "anywhere" }} variant="body1">
      {value}
    </Typography>
    {helperText && (
      <Typography color="text.secondary" sx={{ mt: 0.5 }} variant="caption">
        {helperText}
      </Typography>
    )}
  </Box>
);

const StoreDetails = () => {
  const { storeId } = useParams<{ storeId: string }>();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const storeService = StoreService.getInstance();
  const [store, setStore] = useState<Store | null>(null);
  const [region, setRegion] = useState<Region | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadStore = useCallback(async () => {
    if (!storeId) {
      setError("unknown");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await storeService.getStoreById(Number(storeId));
      if (!response.data) {
        throw new Error();
      }

      setStore(response.data);
      try {
        const regionsResponse = await storeService.getRegionsByCustomerId(response.data.customerId);
        setRegion(
          regionsResponse.data?.find((item) => item.id === response.data?.regionId) ?? null,
        );
      } catch {
        setRegion(null);
      }
    } catch (loadError) {
      setError(loadError instanceof Error && loadError.message ? loadError.message : "unknown");
    } finally {
      setIsLoading(false);
    }
  }, [storeId, storeService]);

  useEffect(() => {
    void loadStore();
  }, [loadStore]);

  useEffect(() => {
    document.title = store?.name ?? t("management.stores.stores.title");
  }, [store?.name, t]);

  const emptyValue = (
    <Typography color="action.disabled" component="span">
      {t("management.devices.devices.empty.notAvailable")}
    </Typography>
  );
  const displayValue = (value: unknown) => (hasDisplayValue(value) ? value : emptyValue);
  const metadataEntries = Object.entries(store?.metadataMap ?? {}).sort(([firstKey], [secondKey]) =>
    firstKey.localeCompare(secondKey),
  );
  const storePosition = store ? getStorePosition(store) : null;

  return (
    <PageShell
      breadcrumbs="admin"
      headerActions={
        store && (
          <Button
            startIcon={<EditIcon />}
            variant="contained"
            onClick={() =>
              void navigate(`${paths.admin.stores}/edit/${store.id}`, {
                state: { returnTo: location.pathname },
              })
            }
          >
            {t("management.stores.stores.actions.edit")}
          </Button>
        )
      }
      icon={<StoreIcon />}
      title={store?.name ?? t("management.stores.stores.title")}
      titleAccessory={<LoadingRing loading={isLoading} />}
    >
      {isLoading && (
        <Box sx={{ py: 8 }}>
          <LoadingState label={t("common.loading")} />
        </Box>
      )}

      {error && (
        <ErrorState
          title={
            error === "unknown" ? t("management.stores.storeDetails.errors.failedLoadStore") : error
          }
          onRetry={() => void loadStore()}
        />
      )}

      {store && !isLoading && !error && (
        <Stack spacing={3} sx={{ mt: 2 }}>
          <Paper sx={{ p: 3 }}>
            <Stack component="dl" spacing={3} sx={{ m: 0 }}>
              <Typography component="dt" sx={sectionHeadingSx} variant="h6">
                <StoreIcon />
                {t("management.stores.addStore.sections.basicInformation")}
              </Typography>
              <Box sx={twoColumnGridSx}>
                <Box>
                  <DetailItem
                    label={t("management.stores.addStore.fields.storeName")}
                    value={store.name}
                  />
                </Box>
                <Box>
                  <DetailItem label={t("management.stores.stores.fields.id")} value={store.id} />
                </Box>
                <Box>
                  <DetailItem
                    label={t("management.stores.addStore.fields.externalId")}
                    value={displayValue(store.externalId)}
                  />
                </Box>
                <Box>
                  <DetailItem
                    label={t("management.stores.addStore.fields.phoneNumber")}
                    value={displayValue(store.phoneNumber)}
                  />
                </Box>
                <Box>
                  <DetailItem
                    helperText={t("management.stores.addStore.helperText.openDate")}
                    label={t("management.stores.addStore.fields.openDate")}
                    value={
                      hasDisplayValue(store.openDate)
                        ? formatStoreDate(store.openDate, store.timezone, i18n.language)
                        : emptyValue
                    }
                  />
                </Box>
                <Box>
                  <DetailItem
                    label={t("management.stores.addStore.fields.group")}
                    value={displayValue(store.group)}
                  />
                </Box>
                <Box sx={{ gridColumn: { md: "span 2" } }}>
                  <StoreLabelPreview alignment="start" store={store} />
                </Box>
              </Box>
            </Stack>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Stack component="dl" spacing={3} sx={{ m: 0 }}>
              <Typography component="dt" sx={sectionHeadingSx} variant="h6">
                <LocationIcon />
                {t("management.stores.addStore.sections.location")}
              </Typography>
              <Box
                sx={{
                  alignItems: "start",
                  display: "grid",
                  gap: 3,
                  gridTemplateColumns:
                    storePosition && GOOGLE_MAPS_KEY
                      ? { lg: "minmax(0, 1fr) minmax(360px, 1fr)", xs: "1fr" }
                      : "1fr",
                }}
              >
                {storePosition && GOOGLE_MAPS_KEY && (
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      height: { lg: 400, xs: 300 },
                    }}
                  >
                    <APIProvider apiKey={GOOGLE_MAPS_KEY} version="weekly">
                      <Map
                        defaultCenter={storePosition}
                        defaultZoom={15}
                        disableDefaultUI
                        mapId={GOOGLE_MAP_ID}
                        style={{ height: "100%", width: "100%" }}
                        zoomControl
                      >
                        {GOOGLE_MAP_ID ? (
                          <AdvancedMarker position={storePosition} title={store.name} />
                        ) : (
                          <Marker position={storePosition} title={store.name} />
                        )}
                      </Map>
                    </APIProvider>
                  </Box>
                )}
                <Box sx={twoColumnGridSx}>
                  <Box sx={{ gridColumn: { md: "span 2" } }}>
                    <DetailItem
                      label={t("management.stores.addStore.fields.streetAddress")}
                      value={displayValue(getStoreAddress(store))}
                    />
                  </Box>
                  <Box sx={{ gridColumn: { md: "span 2" } }}>
                    <Divider />
                  </Box>
                  <Box>
                    <DetailItem
                      label={t("management.stores.editStore.fields.city")}
                      value={displayValue(store.city)}
                    />
                  </Box>
                  <Box>
                    <DetailItem
                      label={t("management.stores.editStore.fields.state")}
                      value={displayValue(store.state)}
                    />
                  </Box>
                  <Box>
                    <DetailItem
                      label={t("management.stores.editStore.fields.country")}
                      value={displayValue(store.country)}
                    />
                  </Box>
                  <Box>
                    <DetailItem
                      label={t("management.stores.editStore.fields.zipCode")}
                      value={displayValue(store.zip)}
                    />
                  </Box>
                  <Box sx={{ gridColumn: { md: "span 2" } }}>
                    <DetailItem
                      helperText={t("management.stores.addStore.helperText.timezone")}
                      label={t("management.stores.addStore.fields.timezone")}
                      value={displayValue(store.timezone)}
                    />
                  </Box>
                </Box>
              </Box>
            </Stack>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Stack component="dl" spacing={3} sx={{ m: 0 }}>
              <Typography component="dt" sx={sectionHeadingSx} variant="h6">
                <LanguageIcon />
                {t("management.stores.addStore.sections.localization")}
              </Typography>
              <Box sx={threeColumnGridSx}>
                <Box>
                  <DetailItem
                    label={t("management.stores.addStore.fields.locale")}
                    value={
                      <Box
                        component="code"
                        sx={{ fontFamily: "monospace", overflowWrap: "anywhere" }}
                      >
                        {displayValue(store.locale)}
                      </Box>
                    }
                  />
                </Box>
                <Box>
                  <DetailItem
                    label={t("management.stores.addStore.fields.currencyCode")}
                    value={
                      <Box
                        component="code"
                        sx={{ fontFamily: "monospace", overflowWrap: "anywhere" }}
                      >
                        {displayValue(store.currencyCode)}
                      </Box>
                    }
                  />
                </Box>
                <Box>
                  <DetailItem
                    helperText={t("management.stores.addStore.helperText.currencySymbol")}
                    label={t("management.stores.addStore.fields.currencySymbol")}
                    value={
                      <Box
                        component="code"
                        sx={{ fontFamily: "monospace", overflowWrap: "anywhere" }}
                      >
                        {displayValue(store.currencySymbol)}
                      </Box>
                    }
                  />
                </Box>
              </Box>
            </Stack>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Stack component="dl" spacing={3} sx={{ m: 0 }}>
              <Typography component="dt" sx={sectionHeadingSx} variant="h6">
                <SettingsIcon />
                {t("management.stores.addStore.sections.storeSettings")}
              </Typography>
              <Box sx={twoColumnGridSx}>
                <Box>
                  <DetailItem
                    helperText={t("management.stores.addStore.helperText.region")}
                    label={t("management.stores.addStore.fields.region")}
                    value={displayValue(region?.name)}
                  />
                </Box>
                <Box>
                  <DetailItem
                    helperText={t("management.stores.addStore.helperText.autoVacate")}
                    label={t("management.stores.storeDetails.fields.autoVacate")}
                    value={
                      <Chip
                        color={store.autoCleanEnabled ? "success" : "default"}
                        icon={store.autoCleanEnabled ? <CheckIcon /> : <CloseIcon />}
                        label={t(
                          store.autoCleanEnabled
                            ? "management.stores.addStore.options.enabled"
                            : "management.stores.addStore.options.disabled",
                        )}
                        size="small"
                        variant="outlined"
                      />
                    }
                  />
                </Box>
              </Box>
            </Stack>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Stack component="dl" spacing={3} sx={{ m: 0 }}>
              <Typography component="dt" sx={sectionHeadingSx} variant="h6">
                <DataObjectIcon />
                {t("management.stores.storeDetails.sections.metadata")}
              </Typography>
              {metadataEntries.length ? (
                <Box sx={twoColumnGridSx}>
                  {metadataEntries.map(([key, value]) => (
                    <Box key={key}>
                      <DetailItem
                        label={formatMetadataKey(key)}
                        value={
                          <Box
                            component="code"
                            sx={{ fontFamily: "monospace", overflowWrap: "anywhere" }}
                          >
                            {displayValue(value)}
                          </Box>
                        }
                      />
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography color="text.secondary">{emptyValue}</Typography>
              )}
            </Stack>
          </Paper>
        </Stack>
      )}
    </PageShell>
  );
};

export default StoreDetails;
